import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Module-level singleton to enforce a single active player
 */
let ACTIVE_PLAYER = null;

export default function RhinoVoipVoicemailPlayer({
  voicemailMessageId, callerIdNumber, autoPlay, onAutoPlayStarted, onPlay,
}) {
  const audioRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | loading | ready | playing | paused | error
  const [error, setError] = useState(null);

  const [audioUrl, setAudioUrl] = useState(null);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubValue, setScrubValue] = useState(0);

  const wasPlayingRef = useRef(false);

  const controllerRef = useRef({
    pause: () => {
      const a = audioRef.current;
      if (a && !a.paused) a.pause();
    },
  });

  useEffect(() => {
    controllerRef.current.pause = () => {
      const a = audioRef.current;
      if (a && !a.paused) a.pause();
    };
  });

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => {
      if (!isScrubbing) setCurrentTime(audio.currentTime || 0);
    };
    const handlePlayEvent = () => setStatus('playing');
    const onPause = () => setStatus((s) => (s === 'loading' ? s : 'paused'));
    const onEnded = () => {
      setStatus('paused');
      setCurrentTime(0);
    };
    const onError = () => {
      setStatus('error');
      setError('Audio failed to load/play.');
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', handlePlayEvent);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', handlePlayEvent);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [isScrubbing]);

  // reset when voicemail row changes
  useEffect(() => {
    setStatus('idle');
    setError(null);
    setDuration(0);
    setCurrentTime(0);
    setIsScrubbing(false);
    setScrubValue(0);
    wasPlayingRef.current = false;

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }

    setAudioUrl(null);

    if (ACTIVE_PLAYER === controllerRef.current) {
      ACTIVE_PLAYER = null;
    }
  }, [voicemailMessageId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoPlay && voicemailMessageId && status === 'idle') {
      togglePlay();
      if (onAutoPlayStarted) onAutoPlayStarted();
    }
  }, [autoPlay, voicemailMessageId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function ensureSignedUrl() {
    if (audioUrl) return audioUrl;

    setStatus('loading');
    setError(null);

    try {
      const base = (process.env.REACT_APP_V3_BASE_URL || '').replace(/\/$/, '');
      const qs = callerIdNumber ? `?caller_id_number=${encodeURIComponent(callerIdNumber)}` : '';
      const apiUrl = `${base}/voip/voicemail/${voicemailMessageId}${qs}`;

      const response = await axios.get(apiUrl, {
        withCredentials: true,
      });

      // your response shape:
      // { object: { url: "https://...signed..." } }
      const signedUrl = response?.data?.object?.url;

      if (!signedUrl) {
        throw new Error('Missing signed URL in API response (expected data.object.url).');
      }

      const audio = audioRef.current;
      if (!audio) throw new Error('No audio element');

      audio.src = signedUrl;
      audio.load();

      setAudioUrl(signedUrl);
      setStatus('ready');
      return signedUrl;
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Failed to load voicemail audio.');
      throw e;
    }
  }

  function pauseAnyOtherPlayer() {
    if (ACTIVE_PLAYER && ACTIVE_PLAYER !== controllerRef.current) {
      ACTIVE_PLAYER.pause();
    }
    ACTIVE_PLAYER = controllerRef.current;
  }

  async function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;

    if (status === 'playing') {
      audio.pause();
      return;
    }

    try {
      pauseAnyOtherPlayer();
      await ensureSignedUrl();
      await audio.play();
      if (typeof onPlay === 'function') onPlay();
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Failed to play voicemail.');
    }
  }

  function fmt(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const s = Math.floor(seconds);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  }

  function onScrubStart() {
    const audio = audioRef.current;
    wasPlayingRef.current = !!audio && !audio.paused;

    setIsScrubbing(true);
    setScrubValue(audio?.currentTime || 0);
  }

  function onScrubChange(e) {
    const v = Number(e.target.value);
    setScrubValue(v);

    const audio = audioRef.current;
    if (audio && Number.isFinite(v)) {
      audio.currentTime = v;
    }
  }

  async function onScrubEnd(e) {
    const v = Number(e.target.value);
    const audio = audioRef.current;

    if (audio && Number.isFinite(v)) {
      audio.currentTime = v;
      setCurrentTime(v);
    }

    setIsScrubbing(false);

    if (audio && wasPlayingRef.current) {
      try {
        await audio.play();
      } catch (err) {
        // ignore
      }
    }

    wasPlayingRef.current = false;
  }

  const isLoading = status === 'loading';
  const isPlaying = status === 'playing';

  const playLabel = isPlaying ? 'Pause voicemail' : 'Play voicemail';

  const buttonText = (() => {
    if (isLoading) return '…';
    if (isPlaying) return '❚❚';
    return '▶';
  })();

  const buttonBg = isPlaying ? '#f3f3f3' : 'white';
  const buttonCursor = isLoading ? 'not-allowed' : 'pointer';

  const disabled = !voicemailMessageId || isLoading || status === 'error';
  const timeValue = isScrubbing ? scrubValue : currentTime;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '90%' }}>
      <button
        type="button"
        onClick={togglePlay}
        disabled={!voicemailMessageId || isLoading}
        aria-label={playLabel}
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          border: '1px solid #ccc',
          background: buttonBg,
          cursor: buttonCursor,
        }}
      >
        {buttonText}
      </button>

      {/* <button
        type="button"
        onClick={downloadVoicemail}
        disabled={!voicemailMessageId || isLoading}
        aria-label="Download voicemail"
        title="Download voicemail"
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          border: '1px solid #ccc',
          background: 'white',
          cursor: !voicemailMessageId || isLoading ? 'not-allowed' : 'pointer',
        }}
      >
        ⬇
      </button> */}

      <div style={{ flex: 1, position: 'relative' }}>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={timeValue}
          onMouseDown={onScrubStart}
          onTouchStart={onScrubStart}
          onChange={onScrubChange}
          onMouseUp={onScrubEnd}
          onTouchEnd={onScrubEnd}
          disabled={disabled || !duration}
          style={{ width: '100%' }}
        />

        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: -14,
            fontFamily: 'monospace',
            fontSize: 11,
            whiteSpace: 'nowrap',
            color: '#555',
          }}
        >
          {fmt(timeValue)} / {fmt(duration)}
        </div>

        {error ? (
          <div style={{ marginTop: 18, fontSize: 12, color: '#b00020' }}>
            {error}
          </div>
        ) : null}
      </div>

      <audio ref={audioRef} preload="metadata">
        <track kind="captions" />
      </audio>
    </div>
  );
}
